import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { DreamTeamTable } from "./components/DreamTeamTable";
import { WelcomePanel } from "./components/WelcomePanel";
import { UpcomingGamesCarousel } from "./components/UpcomingGamesCarousel";
import { RosterColumn } from "./components/RosterColumn";
import { ScoutSidebar } from '../../dashboard/ScoutSidebar';
import { useOutletContext } from 'react-router-dom';
import { fetchScoutTeams, loadMoreScoutTeams, fetchScoutGames, fetchDreamTeamLeftRoster, fetchDreamTeamRightRoster } from '../../../../features/scout/scoutProfileSlice';
import logo from "../../../../assets/images/coachTeamLogo.png";

export const DreamTeamDashboard = () => {
  const { activeView } = useOutletContext();
  const dispatch = useDispatch();
  const {
    scoutTeams,
    scoutTeamsLoading,
    scoutTeamsPagination,
    scoutGames,
    scoutGamesLoading,
    dreamTeamLeftRoster,
    dreamTeamRightRoster,
    dreamTeamLeftTeamId,
    dreamTeamRightTeamId,
    dreamTeamLeftRosterLoading,
    dreamTeamRightRosterLoading
  } = useSelector((state) => state.scoutProfile);

  // Search state
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef(null);

  // Debounce search input - wait 500ms after user stops typing
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 500);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Auto-load featured game teams in rosters
  useEffect(() => {
    if (scoutGames.length > 0 && scoutGames[0]) {
      const featuredGame = scoutGames[0];
      const homeTeamId = featuredGame.homeTeamId?._id;
      const awayTeamId = featuredGame.awayTeamId?._id;

      // Load home team in left roster if different from current
      if (homeTeamId && homeTeamId !== dreamTeamLeftTeamId) {
        dispatch(fetchDreamTeamLeftRoster({ teamId: homeTeamId, limit: 20 }));
      }

      // Load away team in right roster if different from current
      if (awayTeamId && awayTeamId !== dreamTeamRightTeamId) {
        dispatch(fetchDreamTeamRightRoster({ teamId: awayTeamId, limit: 20 }));
      }
    }
  }, [scoutGames, dispatch, dreamTeamLeftTeamId, dreamTeamRightTeamId]);

  // Get team names for roster columns
  const leftTeamName = useMemo(() => {
    if (!dreamTeamLeftTeamId) return "SELECT A TEAM";

    // Try to find in scoutTeams
    const team = scoutTeams.find(t => t._id === dreamTeamLeftTeamId);
    if (team) return team.name || team.teamName;

    // Try to find in scoutGames
    const game = scoutGames.find(g =>
      g.homeTeamId?._id === dreamTeamLeftTeamId || g.awayTeamId?._id === dreamTeamLeftTeamId
    );
    if (game) {
      return game.homeTeamId?._id === dreamTeamLeftTeamId
        ? game.homeTeamId?.name
        : game.awayTeamId?.name;
    }

    return "TEAM 1";
  }, [dreamTeamLeftTeamId, scoutTeams, scoutGames]);

  const rightTeamName = useMemo(() => {
    if (!dreamTeamRightTeamId) return "SELECT A TEAM";

    // Try to find in scoutTeams
    const team = scoutTeams.find(t => t._id === dreamTeamRightTeamId);
    if (team) return team.name || team.teamName;

    // Try to find in scoutGames
    const game = scoutGames.find(g =>
      g.homeTeamId?._id === dreamTeamRightTeamId || g.awayTeamId?._id === dreamTeamRightTeamId
    );
    if (game) {
      return game.homeTeamId?._id === dreamTeamRightTeamId
        ? game.homeTeamId?.name
        : game.awayTeamId?.name;
    }

    return "TEAM 2";
  }, [dreamTeamRightTeamId, scoutTeams, scoutGames]);

  // Get team logos
  const leftTeamLogo = useMemo(() => {
    if (!dreamTeamLeftTeamId) return null;

    // Try to find in scoutTeams
    const team = scoutTeams.find(t => t._id === dreamTeamLeftTeamId);
    if (team?.logo) return team.logo;

    // Try to find in scoutGames
    const game = scoutGames.find(g =>
      g.homeTeamId?._id === dreamTeamLeftTeamId || g.awayTeamId?._id === dreamTeamLeftTeamId
    );
    if (game) {
      return game.homeTeamId?._id === dreamTeamLeftTeamId
        ? game.homeTeamId?.logo
        : game.awayTeamId?.logo;
    }

    return null;
  }, [dreamTeamLeftTeamId, scoutTeams, scoutGames]);

  const rightTeamLogo = useMemo(() => {
    if (!dreamTeamRightTeamId) return null;

    // Try to find in scoutTeams
    const team = scoutTeams.find(t => t._id === dreamTeamRightTeamId);
    if (team?.logo) return team.logo;

    // Try to find in scoutGames
    const game = scoutGames.find(g =>
      g.homeTeamId?._id === dreamTeamRightTeamId || g.awayTeamId?._id === dreamTeamRightTeamId
    );
    if (game) {
      return game.homeTeamId?._id === dreamTeamRightTeamId
        ? game.homeTeamId?.logo
        : game.awayTeamId?.logo;
    }

    return null;
  }, [dreamTeamRightTeamId, scoutTeams, scoutGames]);

  // Fetch teams when debounced search changes
  useEffect(() => {
    dispatch(fetchScoutTeams({
      page: 1,
      limit: 10,
      teamName: debouncedSearch || undefined
    }));
  }, [dispatch, debouncedSearch]);

  // Fetch scout games on component mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    dispatch(fetchScoutGames({
      page: 1,
      limit: 10,
      startDate: today
    }));
  }, [dispatch]);

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (scoutTeamsPagination.hasMore && !scoutTeamsLoading) {
      dispatch(loadMoreScoutTeams({
        page: scoutTeamsPagination.currentPage + 1,
        limit: scoutTeamsPagination.limit,
        teamName: debouncedSearch || undefined
      }));
    }
  }, [dispatch, scoutTeamsPagination, scoutTeamsLoading, debouncedSearch]);

  // Transform teams data for table display
  const transformedTeams = scoutTeams.map((team, index) => ({
    id: team._id || index + 1,
    _id: team._id,  // Keep original _id for API calls
    logo: team.logo || logo,
    name: team.name || team.teamName || 'Unknown Team',
    teamName: team.name || team.teamName || 'Unknown Team',
    ovrRecord: team.overallRecord || team.ovrRecord || '-',
    home: team.homeRecord || team.home || '-',
    away: team.awayRecord || team.away || '-',
    neutral: team.neutralRecord || team.neutral || '-',
    conference: team.conferenceRecord || team.conference || '-',
    // Keep original data for header display
    ...team,
  }));

  // Transform games data for carousel display
  const transformedGames = scoutGames.map(game => {
    const isLive = game.status === 'live';

    // Calculate time remaining for upcoming games
    let timeInfo = '';
    if (!isLive && game.date && game.time) {
      const gameDateTime = new Date(`${game.date.split('T')[0]}T${game.time}:00`);
      const now = new Date();
      const timeDiff = gameDateTime - now;

      if (timeDiff > 0) {
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

        if (days > 0) {
          timeInfo = `${days}d ${hours}h`;
        } else if (hours > 0) {
          timeInfo = `${hours}h ${minutes}m`;
        } else {
          timeInfo = `${minutes}m`;
        }
      }
    }

    return {
      id: game._id,
      homeTeamId: game.homeTeamId?._id,
      awayTeamId: game.awayTeamId?._id,
      homeLogo: game.homeTeamId?.logo || logo,
      awayLogo: game.awayTeamId?.logo || logo,
      home: game.homeTeamId?.name || 'TBD',
      away: game.awayTeamId?.name || 'TBD',
      location: game.location || 'TBD',
      rawDate: game.date, // Keep original date for calculations
      date: game.date ? new Date(game.date).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long'
      }) : 'TBD',
      time: game.time || '',
      isLive,
      timeRemaining: timeInfo,
      streamLink: game.streamLink
    };
  });

  // Separate first game (live or next upcoming) for WelcomePanel
  const featuredGame = transformedGames.length > 0 ? transformedGames[0] : null;

  // Remaining games for carousel
  const upcomingGames = transformedGames.slice(1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mt-6">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <ScoutSidebar activeView={activeView} />
      </div>

      {/* Main Content - Vertical Layout */}
      <div className="lg:col-span-3 w-full h-full flex flex-col gap-4 border border-slate-700/50 rounded-2xl p-4">
        {/* Top Section: Welcome + Upcoming Games */}
        <div className="rounded-3xl bg-slate-950/80 border border-white/10 p-4 flex flex-col gap-4">
          {/* Featured Game in Welcome Panel */}
          {scoutGamesLoading && scoutGames.length === 0 ? (
            <div className="rounded-3xl bg-slate-900/80 p-3 flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <WelcomePanel game={featuredGame} />
          )}

          {/* Remaining Games Carousel */}
          {!scoutGamesLoading && upcomingGames.length > 0 && (
            <UpcomingGamesCarousel games={upcomingGames} />
          )}
        </div>

        {/* Roster Columns (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RosterColumn
            title={leftTeamName}
            players={dreamTeamLeftRoster}
            loading={dreamTeamLeftRosterLoading}
            teamLogo={leftTeamLogo}
          />
          <RosterColumn
            title={rightTeamName}
            players={dreamTeamRightRoster}
            loading={dreamTeamRightRosterLoading}
            teamLogo={rightTeamLogo}
          />
        </div>

        {/* All Teams Table (Full Width) */}
        <div className="rounded-3xl bg-slate-900/70 border border-white/10 p-4 flex flex-col">
          <DreamTeamTable
            teams={transformedTeams}
            title="ALL TEAMS"
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            loading={scoutTeamsLoading}
            pagination={scoutTeamsPagination}
            onLoadMore={handleLoadMore}
            showSearch={true}
          />
        </div>
      </div>
    </div>
  );
};
